/**
 * three/autoFit.js — V2.0.61 monolith split.
 *
 * Pure helper extracted from ThreeViewport.jsx. Frames a Three.js group in
 * the camera viewport via a 28° hero-angle 3/4-view. No side effects beyond
 * mutating the provided `camera` and `controls` (which is the whole point
 * of an auto-fit). Returns `true` on success, `false` on bad input.
 *
 * @param {object} args
 *   @prop {THREE.Object3D} args.THREE  The THREE module instance (pass it in so
 *                                      this helper has no top-level dependency
 *                                      on three's bundle).
 *   @prop {THREE.PerspectiveCamera} args.camera
 *   @prop {OrbitControls} args.controls
 *   @prop {THREE.Group} args.group     The house group.
 * @returns {boolean}
 */
export function runAutoFit({ THREE, camera, controls, group }) {
  if (!camera || !controls || !group || group.children.length === 0) return false;
  try {
    const box = new THREE.Box3().setFromObject(group);
    if (
      !isFinite(box.min.x) || !isFinite(box.max.x) ||
      !isFinite(box.min.z) || !isFinite(box.max.z)
    ) return false;
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    // V2.0.59 — Proper FOV-aware framing. Compute the actual distance
    // needed for the bounding sphere to fit both the vertical and
    // horizontal FOV with 12% padding, then position the camera on a
    // 28° hero-angle pitch for a proper 3/4 view.
    const fovV = (camera.fov || 50) * Math.PI / 180;
    const aspect = camera.aspect || 1;
    const halfW = Math.max(size.x, size.z) / 2;
    const halfV = (size.y + Math.max(size.x, size.z) * 0.18) / 2;
    const distV = halfV / Math.tan(fovV / 2);
    const distH = halfW / (Math.tan(fovV / 2) * aspect);
    const fitDist = Math.max(distV, distH) * 1.12;
    // V2.0.60 — Multi-story safety pad. Tall homes (3+ floors) push
    // the upper floor close to the top of the viewport where the
    // inspector's translucent overlay can clip the roof ridge. Dolly
    // back ~15% per floor above 2 to give the upper levels breathing
    // room.
    const floorCount = group?.userData?.floor_count
      || group?.children?.filter?.((c) => c?.userData?.is_floor)?.length
      || 1;
    const safetyPad = floorCount > 2 ? 1 + 0.15 * (floorCount - 2) : 1;
    const safeFitDist = fitDist * safetyPad;
    const pitch = (28 * Math.PI) / 180;
    const horiz = safeFitDist * Math.cos(pitch);
    const vert = safeFitDist * Math.sin(pitch);
    camera.position.set(
      center.x,
      center.y + vert,
      box.min.z - horiz,
    );
    camera.up.set(0, 1, 0);
    controls.target.set(center.x, center.y + size.y * 0.18, center.z);
    controls.update();
    return true;
  } catch (err) {
    console.debug("[autoFit] failed:", err);
    return false;
  }
}
